import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  cancelFiatWithdrawal,
  fetchFiatWithdrawal,
  fetchFiatWithdrawals,
  formatAedAmount,
} from "../../../api/services/fiatService";
import { useFiatHold } from "../../../customComponents/FiatHoldBanner";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import { exportFiatWithdrawHistoryBrandedExcel } from "../../../utils/exportBrandedHistoryExcel";
import "../WithdrawPage/withdrawal.css";

const HISTORY_PAGE_SIZE = 20;

const TIME_FILTER_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All status" },
  { value: "AWAITING_ADMIN", label: "Under review" },
  { value: "SUBMITTED", label: "Submitted" },
  { value: "INITIATED", label: "Sent to bank" },
  { value: "COMPLETED", label: "Completed" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" },
  { value: "FAILED", label: "Failed" },
  { value: "REVERSED", label: "Reversed" },
];

function isUserCancelled(item, status) {
  if (String(status || item?.status || "").toUpperCase() !== "REJECTED") return false;
  if (String(item?.status_label || "") === "Cancelled") return true;
  const reason = String(item?.reject_reason || item?.status_reason || "").trim();
  return reason === "Cancelled by user" || /^you cancelled this withdrawal/i.test(reason);
}

function statusTone(status, cancelled) {
  if (cancelled) return "neutral";
  if (status === "COMPLETED" || status === "PROCESSED") return "success";
  if (status === "AWAITING_ADMIN" || status === "SUBMITTED" || status === "INITIATED") return "pending";
  if (status === "REJECTED" || status === "FAILED" || status === "REVERSED") return "danger";
  return "neutral";
}

function formatHistDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function last4Label(ben) {
  if (!ben) return "";
  if (ben.iban_masked) return ben.iban_masked;
  if (ben.iban_last4) return `•••• ${ben.iban_last4}`;
  return "";
}

function bankLabel(item) {
  const ben = item?.whitelist || item?.beneficiary || null;
  const fromBen = last4Label(ben);
  if (fromBen) return fromBen;
  const last4 = item?.iban_last4 || item?.creditor_iban_last4 || ben?.iban_last4;
  if (last4) return `•••• ${last4}`;
  return "—";
}

function mapFiatWithdrawRow(item) {
  const status = String(item?.status || "").toUpperCase();
  const cancelled = isUserCancelled(item, status);
  const ben = item?.whitelist || item?.beneficiary || null;
  let statusLabel = item.status_label || status || "—";
  if (cancelled) statusLabel = "Cancelled";
  else if (status === "REJECTED" && (statusLabel === "Declined" || !item.status_label)) {
    statusLabel = "Rejected";
  }
  return {
    id: String(item.id || ""),
    createdAt: item.created_at || null,
    dateLabel: formatHistDate(item.created_at),
    amount: item.amount || "0",
    fee: item.fee_aed || "0",
    net: item.net_aed || item.amount || "0",
    currency: item.currency || "AED",
    status,
    statusLabel,
    statusReason: item.status_reason || "",
    statusTone: statusTone(status, cancelled),
    cancelled,
    canCancel: status === "AWAITING_ADMIN",
    bank: bankLabel(item),
    accountName: ben?.account_name_masked || "",
    bankName: ben?.bank_name || "",
    wallet: item.wallet_type === "spot" || !item.wallet_type ? "Spot" : String(item.wallet_type),
    approvalMode: item.approval_mode === "AUTO" ? "Auto" : "Manual",
    channelRef: item.channel_ref_id || "",
    rejectReason: item.reject_reason || "",
    initiatedLabel: formatHistDate(item.initiated_at),
    finalizedLabel: formatHistDate(item.finalized_at),
    updatedLabel: formatHistDate(item.updated_at),
  };
}

function rowWithinTimeFilter(row, timeFilter) {
  if (!timeFilter || timeFilter === "all") return true;
  if (!row.createdAt) return true;
  const d = new Date(row.createdAt);
  if (Number.isNaN(d.getTime())) return true;
  const days = { "7d": 7, "30d": 30, "90d": 90 }[timeFilter];
  if (!days) return true;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - days);
  return d >= cutoff;
}

function DfFilterDropdown({ id, label, value, options, openKey, menuKey, onOpenToggle, onChange }) {
  const open = openKey === menuKey;
  const selected = options.find((o) => o.value === value) || options[0];

  return (
    <div className={`df-dd${open ? " is-open" : ""}`} data-df-dropdown>
      <span className="df-dd-label" id={`${id}-lbl`}>
        {label}
      </span>
      <button
        type="button"
        id={id}
        className={`df-dd-trigger${open ? " is-open" : ""}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={`${id}-lbl`}
        onClick={(e) => {
          e.stopPropagation();
          onOpenToggle(open ? null : menuKey);
        }}
      >
        <span className="df-dd-value">{selected.label}</span>
        <svg className="df-dd-chevron" width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
          <path fill="currentColor" d="M1.4.6 6 5.2 10.6.6 12 2 6 8 0 2z" />
        </svg>
      </button>
      {open ? (
        <ul className="df-dd-menu" role="listbox" aria-labelledby={`${id}-lbl`}>
          {options.map((opt) => (
            <li key={opt.value} role="option" aria-selected={opt.value === value}>
              <button
                type="button"
                className={`df-dd-option${opt.value === value ? " is-selected" : ""}`}
                onClick={() => {
                  onChange(opt.value);
                  onOpenToggle(null);
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function buildWithdrawSteps(row) {
  const requested = { title: "Withdrawal requested", time: row.dateLabel, emphasized: false };
  if (row.cancelled) {
    return [requested, { title: "Cancelled", time: row.finalizedLabel || row.dateLabel, emphasized: true }];
  }
  if (row.status === "REJECTED") {
    return [requested, { title: "Rejected", time: row.finalizedLabel || row.dateLabel, emphasized: true }];
  }
  if (row.status === "FAILED") {
    return [requested, { title: "Failed", time: row.finalizedLabel || row.dateLabel, emphasized: true }];
  }
  if (row.status === "REVERSED") {
    return [requested, { title: "Reversed", time: row.finalizedLabel || row.dateLabel, emphasized: true }];
  }
  if (row.status === "COMPLETED" || row.status === "PROCESSED") {
    return [
      requested,
      { title: "Sent to bank", time: row.initiatedLabel || row.dateLabel, emphasized: false },
      { title: "Completed", time: row.finalizedLabel || row.dateLabel, emphasized: true },
    ];
  }
  if (row.status === "INITIATED") {
    return [requested, { title: "Sent to bank", time: row.initiatedLabel || row.dateLabel, emphasized: true }];
  }
  if (row.status === "SUBMITTED") {
    return [requested, { title: "Submitted to bank", time: row.dateLabel, emphasized: true }];
  }
  return [requested, { title: row.statusLabel || "Under review", time: row.dateLabel, emphasized: true }];
}

async function copyText(text) {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

function FiatWithdrawDetailModal({ open, row, cancelling, onClose, onCancel }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !row) return null;

  const steps = buildWithdrawSteps(row);
  const stepperOutcome =
    row.statusTone === "success" ? "success" : row.statusTone === "danger" ? "danger" : "pending";
  const bankLine = [row.bank, row.bankName].filter((v) => v && v !== "—").join(" · ");
  const fields = [
    { label: "Status", value: row.statusLabel },
    { label: "Date", value: row.dateLabel },
    { label: "Withdraw amount", value: `${formatAedAmount(row.amount)} ${row.currency}` },
    { label: "Fee", value: `${formatAedAmount(row.fee)} ${row.currency}` },
    { label: "Sent to bank", value: `${formatAedAmount(row.net)} ${row.currency}` },
    { label: "Wallet", value: row.wallet },
    { label: "Bank account", value: bankLine },
    { label: "Account name", value: row.accountName },
    { label: "Approval", value: row.approvalMode },
    { label: "Reference", value: row.id, copy: true },
    { label: "Bank reference", value: row.channelRef, copy: true },
    { label: "Sent at", value: row.initiatedLabel },
    { label: "Finalized", value: row.finalizedLabel },
  ].filter((f) => f.value && f.value !== "—");

  return (
    <div className="withdraw_confirm_overlay withdraw_hist_detail_overlay" role="presentation" onClick={onClose}>
      <div
        className="withdraw_hist_detail_modal wf-fiat-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wf-fiat-wd-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <div className="withdraw_hist_detail_head">
          <h2 id="wf-fiat-wd-detail-title" className="withdraw_hist_detail_title">
            Withdrawal details
          </h2>
        </div>
        <div className="withdraw_hist_detail_body">
          <div className="withdraw_hist_stepper" aria-label="Withdrawal progress" data-outcome={stepperOutcome}>
            {steps.map((s, i, arr) => (
              <div key={`${row.id}-wstep-${i}`} className="withdraw_hist_step">
                <div className="withdraw_hist_step_track" aria-hidden="true">
                  <span className="withdraw_hist_step_dot" />
                  {i < arr.length - 1 ? <span className="withdraw_hist_step_line" /> : null}
                </div>
                <div className="withdraw_hist_step_text">
                  <div className={`withdraw_hist_step_title${s.emphasized ? " is-emphasized" : ""}`}>{s.title}</div>
                  <div className="withdraw_hist_step_time">{s.time}</div>
                </div>
              </div>
            ))}
          </div>
          {row.statusReason ? (
            <p
              className={`withdraw_hist_step_note${row.statusTone === "danger" || row.cancelled ? " withdraw_hist_step_note--danger" : ""
                }`}
            >
              {row.statusReason}
            </p>
          ) : null}
          <div className="withdraw_hist_rows">
            {fields.map((f) => (
              <div key={f.label} className="withdraw_hist_row">
                <span className="withdraw_hist_row_label">{f.label}</span>
                <span className="withdraw_hist_row_value">
                  {f.copy ? (
                    <>
                      {f.value}
                      <button
                        type="button"
                        className="df-deposit-history-copy-btn"
                        onClick={async () => {
                          const ok = await copyText(f.value);
                          if (ok) alertSuccessMessage("Copied");
                        }}
                        aria-label={`Copy ${f.label}`}
                      >
                        <i className="ri-file-copy-line" aria-hidden />
                      </button>
                    </>
                  ) : (
                    f.value
                  )}
                </span>
              </div>
            ))}
          </div>
          {row.canCancel ? (
            <button type="button" className="df-submit-btn wf-fiat-detail-cancel" disabled={cancelling} onClick={() => onCancel(row)}>
              {cancelling ? "Cancelling…" : "Cancel withdrawal"}
            </button>
          ) : null}
          <div className="withdraw_hist_help_links wf-fiat-detail-help">
            <Link to="/user_profile/create_tickets" className="withdraw_hist_gold_link">
              Need help? Chat with us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function FiatWithdrawCancelConfirm({ open, row, cancelling, onClose, onConfirm }) {
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open || !row) return null;

  return (
    <div className="df-kyc-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="df-kyc-modal wf-cancel-confirm"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wf-cancel-confirm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="df-kyc-modal-close" aria-label="Close" onClick={onClose}>
          <i className="ri-close-line" aria-hidden="true" />
        </button>
        <h2 id="wf-cancel-confirm-title" className="df-kyc-modal-title">
          Cancel this withdrawal?
        </h2>
        <p className="df-kyc-modal-text">
          {formatAedAmount(row.amount)} {row.currency} will return to your spot wallet. This cannot be undone.
        </p>
        <div className="wf-cancel-confirm-actions">
          <button type="button" className="wf-cancel-confirm-keep" onClick={onClose} disabled={cancelling}>
            Keep withdrawal
          </button>
          <button type="button" className="wf-cancel-confirm-yes" onClick={() => onConfirm(row)} disabled={cancelling}>
            {cancelling ? "Cancelling…" : "Cancel withdrawal"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WithdrawFiatHistorySection({ refreshKey = 0 }) {
  const hold = useFiatHold();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [timeFilter, setTimeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [cancellingId, setCancellingId] = useState("");

  const loadPage = useCallback(
    async (nextPage, replace) => {
      if (replace) {
        setLoading(true);
        setError(null);
      } else {
        setLoadingMore(true);
      }
      const res = await fetchFiatWithdrawals({
        page: nextPage,
        limit: HISTORY_PAGE_SIZE,
        status:
          statusFilter === "all"
            ? undefined
            : statusFilter === "CANCELLED"
              ? "REJECTED"
              : statusFilter,
      });
      if (!res.ok) {
        if (replace) {
          setRows([]);
          setError(res.error?.message || "Could not load withdrawals history.");
        }
        setHasMore(false);
        setLoading(false);
        setLoadingMore(false);
        return;
      }
      const mapped = (res.items || []).map(mapFiatWithdrawRow);
      setRows((prev) => (replace ? mapped : [...prev, ...mapped]));
      setPage(nextPage);
      setHasMore(!!res.meta?.has_more);
      setLoading(false);
      setLoadingMore(false);
    },
    [statusFilter]
  );

  useEffect(() => {
    loadPage(1, true);
  }, [loadPage, refreshKey]);

  useEffect(() => {
    if (!openDropdown) return undefined;
    const onDocClick = (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      if (!t.closest("[data-df-dropdown]")) setOpenDropdown(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    document.addEventListener("click", onDocClick);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [openDropdown]);

  const filteredRows = useMemo(
    () =>
      rows.filter((row) => {
        if (!rowWithinTimeFilter(row, timeFilter)) return false;
        if (statusFilter === "CANCELLED") return row.cancelled;
        if (statusFilter === "REJECTED") return row.status === "REJECTED" && !row.cancelled;
        return true;
      }),
    [rows, timeFilter, statusFilter]
  );

  const openDetail = useCallback(async (row) => {
    setDetailRow(row);
    setDetailOpen(true);
    const res = await fetchFiatWithdrawal(row.id);
    if (res.ok && res.withdrawal) {
      setDetailRow(mapFiatWithdrawRow(res.withdrawal));
    }
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailRow(null);
  }, []);

  const requestCancel = useCallback(
    (row) => {
      if (!row?.canCancel || hold.held) return;
      setConfirmRow(row);
    },
    [hold.held]
  );

  const closeCancelConfirm = useCallback(() => {
    if (cancellingId) return;
    setConfirmRow(null);
  }, [cancellingId]);

  const handleCancel = useCallback(
    async (row) => {
      if (!row?.canCancel || hold.held) return;
      setCancellingId(row.id);
      const res = await cancelFiatWithdrawal(row.id);
      setCancellingId("");
      if (!res.ok) {
        alertErrorMessage(res.error?.message || "Could not cancel withdrawal.");
        return;
      }
      const mapped = mapFiatWithdrawRow(res.withdrawal || { ...row, status: "REJECTED" });
      setRows((prev) => prev.map((r) => (r.id === row.id ? mapped : r)));
      setDetailRow(mapped);
      setConfirmRow(null);
      alertSuccessMessage(res.withdrawal?.status_label || "Withdrawal cancelled.");
    },
    [hold.held]
  );

  const handleExportExcel = useCallback(async () => {
    if (!filteredRows.length) {
      alertErrorMessage("No withdrawals history to export.");
      return;
    }
    try {
      const timeLabel = TIME_FILTER_OPTIONS.find((o) => o.value === timeFilter)?.label || "All time";
      const statusLabel = STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)?.label || "All status";
      await exportFiatWithdrawHistoryBrandedExcel(filteredRows, { timeLabel, statusLabel });
      alertSuccessMessage("Withdrawals history exported.");
    } catch {
      alertErrorMessage("Could not export withdrawals history.");
    }
  }, [filteredRows, timeFilter, statusFilter]);

  const bindRowOpen = (row) => ({
    tabIndex: 0,
    role: "button",
    onClick: () => openDetail(row),
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openDetail(row);
      }
    },
  });

  return (
    <>
      <section className="df-deposit-history-section depositfiathistory" aria-labelledby="wf-withdraw-history-title">
        <div className="container">
          <div className="df-deposit-history-top">
            <h2 id="wf-withdraw-history-title" className="df-deposit-history-title">
              Withdrawals History
            </h2>
            <div className="df-deposit-history-filters" role="search" aria-label="Filter withdrawals history">
              <DfFilterDropdown
                id="wf-hist-time"
                label="Time"
                value={timeFilter}
                options={TIME_FILTER_OPTIONS}
                menuKey="time"
                openKey={openDropdown}
                onOpenToggle={setOpenDropdown}
                onChange={setTimeFilter}
              />
              <DfFilterDropdown
                id="wf-hist-status"
                label="Status"
                value={statusFilter}
                options={STATUS_FILTER_OPTIONS}
                menuKey="status"
                openKey={openDropdown}
                onOpenToggle={setOpenDropdown}
                onChange={setStatusFilter}
              />
              <button
                type="button"
                className="df-deposit-history-export-btn"
                onClick={handleExportExcel}
                disabled={loading || filteredRows.length === 0}
              >
                Export Excel
              </button>
            </div>
          </div>

          <div className="df-deposit-history-card">
            <div className="df-deposit-history-table-wrap" role="region" aria-label="Withdrawals history table">
              {error ? (
                <div className="df-deposit-history-status" role="status">
                  <p>{error}</p>
                  <button type="button" className="df-deposit-history-export-btn" onClick={() => loadPage(1, true)}>
                    Try again
                  </button>
                </div>
              ) : null}
              {loading && rows.length === 0 && !error ? (
                <div className="df-sk-table-wrap" aria-label="Loading withdrawals history">
                  {Array.from({ length: 6 }, (_, i) => (
                    <div className="df-sk-row" key={i}>
                      <div className="df-sk-coin">
                        <span className="df-sk-circle df-sk-glass" />
                        <div className="df-sk-coin-txt">
                          <span className="df-sk-glass df-sk-bone--xs" />
                          <span className="df-sk-glass df-sk-bone--xxs" />
                        </div>
                      </div>
                      <span className="df-sk-glass df-sk-bone--cell" />
                      <span className="df-sk-glass df-sk-bone--cell" />
                      <span className="df-sk-glass df-sk-bone--cell df-sk-w50" />
                    </div>
                  ))}
                </div>
              ) : null}
              {!loading && filteredRows.length === 0 && !error ? (
                <div className="df-deposit-history-empty" aria-live="polite">
                  <img src="/images/no-data.svg" alt="" className="df-deposit-history-empty-img" width={120} height={120} />
                  <p className="df-deposit-history-empty-copy">No AED withdrawals yet.</p>
                </div>
              ) : null}
              {filteredRows.length > 0 ? (
                <>
                  <div className="df-deposit-history-desktop df-deposit-history-desktop--fiat df-deposit-history-desktop--withdraw">
                    <div className="df-deposit-history-head-wrap">
                      <div className="df-deposit-history-head" role="row">
                        {["Date", "Amount", "Fee", "Net", "Bank", "Status", "Action"].map((label) => (
                          <div key={label} className="df-deposit-history-head-cell" role="columnheader">
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="df-deposit-history-body-wrap" aria-label="Recent withdrawals">
                      {filteredRows.map((row) => (
                        <div key={row.id} className="df-deposit-history-row" role="row" {...bindRowOpen(row)}>
                          <div className="df-deposit-history-row-cell" title={row.dateLabel}>
                            {row.dateLabel}
                          </div>
                          <div className="df-deposit-history-row-cell">
                            {formatAedAmount(row.amount)} {row.currency}
                          </div>
                          <div className="df-deposit-history-row-cell">
                            {formatAedAmount(row.fee)} {row.currency}
                          </div>
                          <div className="df-deposit-history-row-cell">
                            {formatAedAmount(row.net)} {row.currency}
                          </div>
                          <div className="df-deposit-history-row-cell" title={[row.bank, row.bankName, row.accountName].filter(Boolean).join(" · ")}>
                            {row.bank}
                          </div>
                          <div className="df-deposit-history-row-cell">
                            <span className={`df-deposit-history-action-badge is-${row.statusTone}`}>
                              {row.statusLabel}
                            </span>
                          </div>
                          <div className="df-deposit-history-row-cell df-deposit-history-row-cell--action">
                            <button
                              type="button"
                              className="wf-history-review-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(row);
                              }}
                            >
                              Review
                            </button>
                            {row.canCancel ? (
                              <button
                                type="button"
                                className="wf-history-cancel-btn"
                                disabled={hold.held || cancellingId === row.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  requestCancel(row);
                                }}
                              >
                                {cancellingId === row.id ? "Cancelling…" : "Cancel"}
                              </button>
                            ) : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="df-deposit-history-mobile" aria-label="Recent withdrawals">
                    {filteredRows.map((row) => (
                      <div key={row.id} className="df-deposit-history-mobile-card" {...bindRowOpen(row)}>
                        <div className="df-deposit-history-mobile-top">
                          <span className="df-deposit-history-mobile-date">{row.dateLabel}</span>
                          <span className={`df-deposit-history-action-badge is-${row.statusTone}`}>
                            {row.statusLabel}
                          </span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Amount</span>
                          <span className="df-deposit-history-mobile-value">
                            {formatAedAmount(row.amount)} {row.currency}
                          </span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Fee</span>
                          <span className="df-deposit-history-mobile-value">
                            {formatAedAmount(row.fee)} {row.currency}
                          </span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Net</span>
                          <span className="df-deposit-history-mobile-value">
                            {formatAedAmount(row.net)} {row.currency}
                          </span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Bank</span>
                          <span className="df-deposit-history-mobile-value">{row.bank}</span>
                        </div>
                        <div className="df-deposit-history-mobile-row">
                          <span className="df-deposit-history-mobile-label">Action</span>
                          <span className="df-deposit-history-mobile-value wf-history-mobile-actions">
                            <button
                              type="button"
                              className="wf-history-review-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(row);
                              }}
                            >
                              Review
                            </button>
                            {row.canCancel ? (
                              <button
                                type="button"
                                className="wf-history-cancel-btn"
                                disabled={hold.held || cancellingId === row.id}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  requestCancel(row);
                                }}
                              >
                                {cancellingId === row.id ? "Cancelling…" : "Cancel"}
                              </button>
                            ) : null}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : null}
              {hasMore && !error ? (
                <div className="df-deposit-history-more">
                  <button
                    type="button"
                    className="df-deposit-history-export-btn"
                    onClick={() => loadPage(page + 1, false)}
                    disabled={loadingMore}
                  >
                    {loadingMore ? "Loading…" : "Load more"}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <FiatWithdrawDetailModal
        open={detailOpen}
        row={detailRow}
        cancelling={cancellingId === detailRow?.id}
        onClose={closeDetail}
        onCancel={requestCancel}
      />
      <FiatWithdrawCancelConfirm
        open={!!confirmRow}
        row={confirmRow}
        cancelling={cancellingId === confirmRow?.id}
        onClose={closeCancelConfirm}
        onConfirm={handleCancel}
      />
    </>
  );
}
